import { useState } from 'react';
import { Button, Input } from '@fluentui/react-components';
import { Person24Regular } from '@fluentui/react-icons';
import { parseUserSelectorValue, formatUserSelectorValue } from '@moko/utils';
import { useOtherInfoContext } from '../OtherInfoContext';
import { isFieldReadOnly } from '../fieldReadOnly';
import { FieldWrapper } from './FieldWrapper';
import type { FieldComponentProps } from './types';
import UserOrGroupSelector from '../../UsersOrGroupSelectors/UsersOrGroupSelectors';

export const UserSelectorField = ({
  field,
  fieldValue,
  isFieldDisabled,
  isSaved,
  validationMessage,
}: FieldComponentProps) => {
  const { params, handleOtherInfoChange } = useOtherInfoContext();
  const { onFetchUsers } = params;
  const [pickerOpen, setPickerOpen] = useState(false);

  const parsed = parseUserSelectorValue(fieldValue);
  const display = parsed?.fullName ?? '';
  const readOnly = isSaved || isFieldReadOnly(field);
  const disabled = isFieldDisabled;

  return (
    <FieldWrapper field={field} validationMessage={validationMessage}>
      <div style={{ display: 'flex', gap: 4, width: '100%' }}>
        <Input
          id={field.name}
          type="text"
          readOnly
          disabled={disabled}
          value={display}
          style={{ flex: 1, minWidth: 0 }}
          placeholder={readOnly ? '' : 'Click to pick a user'}
          onClick={() => {
            if (!readOnly && !disabled) setPickerOpen(true);
          }}
        />
        <Button
          appearance="subtle"
          icon={<Person24Regular />}
          disabled={readOnly || disabled}
          onClick={() => setPickerOpen(true)}
          aria-label={`Pick user for ${field.label ?? field.name}`}
        />
      </div>
      {!readOnly && !disabled && (
        <UserOrGroupSelector
          isOpen={pickerOpen}
          hideUsersAndGroupsSelector={() => setPickerOpen(false)}
          userOrGroupOnly="user"
          fromRefUserValues={false}
          getUsers={onFetchUsers}
          onUserOrGroupSelected={(id, _isGroup, username, _name, item) => {
            handleOtherInfoChange(
              field.name,
              formatUserSelectorValue({
                id,
                firstName: item?.firstName,
                lastName: item?.lastName,
                username: username ?? item?.username ?? '',
              })
            );
            setPickerOpen(false);
          }}
        />
      )}
    </FieldWrapper>
  );
};
